import { useState, useEffect } from "react";

export type HeaderTheme = "light" | "dark" | "hero";

/**
 * Detects which section the header overlaps using IntersectionObserver.
 * Sections must have data-header-theme="dark" | "light" | "hero".
 *
 * Fixes over v1:
 * - MutationObserver re-scans for [data-header-theme] elements added/removed
 *   after mount (lazy/Suspense sections, route content) — v1 only queried once.
 * - Observation band is a fixed pixel strip under the header (not 5% of the
 *   viewport), so the switch point is consistent across screen sizes.
 * - Theme is resolved from the section whose top edge actually sits above the
 *   probe line (closest wins), instead of "last in DOM order" — symmetric
 *   behaviour scrolling up and down.
 */

// Vertical probe line, px from viewport top. Header is 80–96px tall;
// the colour flips when a section boundary crosses ~the header midpoint.
const PROBE_PX = 48;

export function useHeaderTheme(): HeaderTheme {
  const [theme, setTheme] = useState<HeaderTheme>("light");

  useEffect(() => {
    const visible = new Set<HTMLElement>();
    let observer: IntersectionObserver | null = null;
    let raf = 0;
    let resizeTimer: number | undefined;

    // DOM-order tie-break: when two sections have the same top (e.g. <main>
    // and the hero section both at y=0), the later/deeper element wins.
    const isAfterInDom = (a: HTMLElement, b: HTMLElement) =>
      (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

    const resolve = () => {
      raf = 0;
      let bestEl: HTMLElement | null = null;
      let bestTop = -Infinity;
      let fallbackEl: HTMLElement | null = null;
      let fallbackTop = Infinity;

      // Fresh rects at resolve time — IO entry rects go stale for sections
      // that stay intersecting across many scroll events (e.g. <main>), which
      // previously made "light" win over the hero back at the top of the page.
      // Candidates change only when an edge crosses the probe line, and every
      // such crossing fires an IO event, so reading here is always accurate.
      visible.forEach((el) => {
        if (!el.isConnected) {
          visible.delete(el);
          return;
        }
        const rect = el.getBoundingClientRect();
        // Primary: section actually containing the probe line
        if (rect.top <= PROBE_PX && rect.bottom > PROBE_PX) {
          if (
            !bestEl ||
            rect.top > bestTop ||
            (rect.top === bestTop && isAfterInDom(bestEl, el))
          ) {
            bestEl = el;
            bestTop = rect.top;
          }
        } else if (rect.top < fallbackTop) {
          // Fallback: topmost visible section (covers iOS rubber-band
          // overscroll, when everything is pushed below the probe line)
          fallbackEl = el;
          fallbackTop = rect.top;
        }
      });

      const value = (bestEl ?? fallbackEl)?.dataset.headerTheme;
      setTheme(value === "dark" || value === "hero" ? value : "light");
    };

    const scheduleResolve = () => {
      if (!raf) raf = requestAnimationFrame(resolve);
    };

    const onIntersect: IntersectionObserverCallback = (entries) => {
      for (const entry of entries) {
        const el = entry.target as HTMLElement;
        if (entry.isIntersecting) {
          visible.add(el);
        } else {
          visible.delete(el);
        }
      }
      scheduleResolve();
    };

    const observeAll = () => {
      document
        .querySelectorAll<HTMLElement>("[data-header-theme]")
        .forEach((el) => observer?.observe(el));
    };

    const buildObserver = () => {
      observer?.disconnect();
      visible.clear();
      // Band = top PROBE_PX pixels of the viewport. rootMargin must be px
      // (computed from innerHeight) — rebuilt on resize below.
      const bottomMargin = Math.max(window.innerHeight - PROBE_PX, 0);
      observer = new IntersectionObserver(onIntersect, {
        rootMargin: `0px 0px -${bottomMargin}px 0px`,
        threshold: 0,
      });
      observeAll();
    };

    buildObserver();

    // Catch sections mounted/unmounted after the initial scan
    // (lazy components, Suspense boundaries, conditional content).
    const mo = new MutationObserver((mutations) => {
      let changed = false;
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches("[data-header-theme]")) {
            observer?.observe(n);
            changed = true;
          }
          n.querySelectorAll<HTMLElement>("[data-header-theme]").forEach((el) => {
            observer?.observe(el);
            changed = true;
          });
        });
        m.removedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          const els: HTMLElement[] = [];
          if (n.matches("[data-header-theme]")) els.push(n);
          n.querySelectorAll<HTMLElement>("[data-header-theme]").forEach((el) =>
            els.push(el)
          );
          for (const el of els) {
            observer?.unobserve(el);
            if (visible.delete(el)) changed = true;
          }
        });
      }
      if (changed) scheduleResolve();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // px rootMargin depends on viewport height → rebuild after resize settles.
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildObserver, 150);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      window.clearTimeout(resizeTimer);
      if (raf) cancelAnimationFrame(raf);
      mo.disconnect();
      observer?.disconnect();
    };
  }, []);

  return theme;
}
